import React from 'react';
import heroImage from '../assets/images/changshun_swim_hero_1785687907536.jpg';
import { Trophy, Users, Award, ShieldCheck, ArrowRight, Search, Sparkles, Bell } from 'lucide-react';

interface HeroSectionProps {
  onNavigate: (tab: string) => void;
  announcement: string;
  stats: { events: number; members: number; certificates: number; trainings: number };
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onNavigate, announcement, stats }) => {
  return (
    <div className="relative bg-slate-900 overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={heroImage} alt="长顺县全民健身中心游泳馆"
          className="w-full h-full object-cover object-center opacity-30 scale-105 transform hover:scale-100 transition-transform duration-1000"
          referrerPolicy="no-referrer" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-900/90 to-sky-950/80" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-sky-500/20 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-500/20 border border-sky-400/30 text-sky-300 text-xs font-semibold backdrop-blur-md">
              <Sparkles className="w-3.5 h-3.5 text-sky-300 animate-pulse" />
              <span>长顺县全民健身与水上运动指导中心</span>
            </div>

            {announcement && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-400/30 text-amber-200 text-xs font-semibold backdrop-blur-md max-w-2xl">
                <Bell className="w-4 h-4 text-amber-300 animate-pulse shrink-0" />
                <span className="truncate">{announcement}</span>
              </div>
            )}

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
              长顺县游泳协会 <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-cyan-300 to-blue-200">构建安全·健康·卓越的水上家园</span>
            </h1>

            <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-2xl">
              贯穿全民健身国家战略，提供游泳赛事组织报名、会员服务、等级与资质证书权威查询、个人水上训练数据看板，以及全县防溺水科普宣传。在线注册后即可报名赛事、缴纳会费、打卡训练。
            </p>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button onClick={() => onNavigate('events')}
                className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-sky-500/25 transition-all transform hover:-translate-y-0.5 flex items-center gap-2">
                <span>赛事在线报名入口</span><ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => onNavigate('certificates')}
                className="px-5 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold text-sm rounded-xl border border-white/20 backdrop-blur-md transition-all flex items-center gap-2">
                <Search className="w-4 h-4 text-sky-300" /><span>证书验证与查询</span>
              </button>
            </div>

            <div className="pt-4 border-t border-slate-800/80">
              <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                <span className="text-sky-400 font-bold">快捷服务：</span>
                <button onClick={() => onNavigate('safety')} className="hover:text-white underline underline-offset-4">防溺水自救技巧</button>
                <span>•</span>
                <button onClick={() => onNavigate('stats')} className="hover:text-white underline underline-offset-4">打卡个人 SWOLF 训练</button>
                <span>•</span>
                <button onClick={() => onNavigate('payments')} className="hover:text-white underline underline-offset-4">缴纳年度会员费</button>
              </div>
            </div>
          </div>

          {/* 真实数据统计 */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            <div className="bg-slate-800/80 backdrop-blur-md p-5 rounded-2xl border border-slate-700/60 shadow-xl hover:border-sky-500/50 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Users className="w-5 h-5" /></div>
              <div className="text-2xl font-bold text-white tracking-tight">{stats.members}+ 人</div>
              <div className="text-xs text-slate-400 mt-1">注册认证游泳会员</div>
            </div>
            <div className="bg-slate-800/80 backdrop-blur-md p-5 rounded-2xl border border-slate-700/60 shadow-xl hover:border-sky-500/50 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Trophy className="w-5 h-5" /></div>
              <div className="text-2xl font-bold text-white tracking-tight">{stats.events} 场</div>
              <div className="text-xs text-slate-400 mt-1">已发布水上赛事</div>
            </div>
            <div className="bg-slate-800/80 backdrop-blur-md p-5 rounded-2xl border border-slate-700/60 shadow-xl hover:border-sky-500/50 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><Award className="w-5 h-5" /></div>
              <div className="text-2xl font-bold text-white tracking-tight">{stats.certificates}+ 张</div>
              <div className="text-xs text-slate-400 mt-1">可核验认证证书</div>
            </div>
            <div className="bg-slate-800/80 backdrop-blur-md p-5 rounded-2xl border border-slate-700/60 shadow-xl hover:border-sky-500/50 transition-colors group">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-400 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform"><ShieldCheck className="w-5 h-5" /></div>
              <div className="text-2xl font-bold text-white tracking-tight">{stats.trainings} 次</div>
              <div className="text-xs text-slate-400 mt-1">会员游泳训练打卡</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
